import { useState, useEffect } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useForm } from "react-hook-form";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { institutionsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Home, Building2, CheckCircle } from "lucide-react";
import {
  INSTITUTION_TYPES,
  INSTITUTION_LEVELS,
  BOARDS,
  MEDIUM_OF_INSTRUCTION,
  DEPARTMENTS,
  FACILITIES,
  AFFILIATION_STATUS,
  ACCREDITATION,
  INDIAN_STATES,
  TRUSTEE_DESIGNATIONS,
  INSTITUTION_ONBOARDING_STEPS,
} from "../constants/institutionFormFields";

export default function InstitutionOnboarding() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  /** Full registration payload from last submit (all steps combined) — set on final submit */
  const [lastRegistrationPayload, setLastRegistrationPayload] = useState<Record<
    string,
    any
  > | null>(null);
  const navigate = usePrefixedNavigate();
  const { user } = useAuth();

  const form = useForm();

  useEffect(() => {
    if (!user) {
      navigate("/auth?portal=institution");
    }
  }, [user, navigate]);

  // Log when full registration payload is stored (after final submit)
  useEffect(() => {
    if (lastRegistrationPayload) {
      console.log(
        "[Institution Onboarding] lastRegistrationPayload (from state):",
        lastRegistrationPayload
      );
    }
  }, [lastRegistrationPayload]);

  const handleNext = (data: any) => {
    const merged = { ...formData, ...data };
    setFormData(merged);
    const nextStep = step + 1;
    console.log(
      `[Institution Onboarding] Step ${step} completed — data:`,
      data
    );
    console.log(
      `[Institution Onboarding] Accumulated formData after step ${step}:`,
      merged
    );
    console.log(`[Institution Onboarding] Moving to step ${nextStep}`);
    form.reset();
    setStep(nextStep);
  };

  const handleBack = () => {
    console.log(
      `[Institution Onboarding] Going back from step ${step} to step ${step - 1}`
    );
    setStep(step - 1);
    form.reset();
  };

  const handleFinalSubmit = async (data: any) => {
    const finalData = {
      ...formData,
      ...data,
      selectedDepartments,
      selectedFacilities,
      selectedLevels,
    };

    // Store full registration payload in one state and log for debugging
    const registrationPayload = {
      step: 5,
      stepName: "Trustee / Management Information",
      submittedAt: new Date().toISOString(),
      formData: finalData,
    };
    setLastRegistrationPayload(registrationPayload);
    console.log("[Institution Onboarding] Step 5 (final) form data:", data);
    console.log(
      "[Institution Onboarding] Full registration payload (all steps) — stored in state:",
      registrationPayload
    );
    console.log(
      "[Institution Onboarding] Complete registration data (JSON):",
      JSON.stringify(registrationPayload, null, 2)
    );

    try {
      if (!user) {
        toast.error("User not authenticated");
        return;
      }

      await institutionsAPI.create(finalData);
      toast.success("Your institution registration is pending approval.");
      navigate("/institution-admin/dashboard");
    } catch (error: any) {
      console.error("Error:", error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        "Failed to register institution";
      toast.error(errorMessage);
    }
  };

  const toggleDepartment = (dept: string) => {
    setSelectedDepartments(prev =>
      prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]
    );
  };

  const toggleFacility = (facility: string) => {
    setSelectedFacilities(prev =>
      prev.includes(facility)
        ? prev.filter(f => f !== facility)
        : [...prev, facility]
    );
  };

  const toggleLevel = (level: string) => {
    setSelectedLevels(prev =>
      prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]
    );
  };

  const currentStepInfo = INSTITUTION_ONBOARDING_STEPS.find(
    s => s.step === step
  );

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <Home className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  JBF Portal
                </h1>
                <p className="text-xs text-muted-foreground">
                  Institution Onboarding
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <Card>
            <CardHeader>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-600">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle>Institution Registration</CardTitle>
                  <CardDescription>
                    Step {step} of 5 - {currentStepInfo?.title}
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {INSTITUTION_ONBOARDING_STEPS.map(s => (
                  <div
                    key={s.step}
                    className={`h-2 flex-1 rounded-full transition-colors ${
                      s.step <= step ? "bg-primary" : "bg-muted"
                    }`}
                  />
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {/* Step 1: Basic Information */}
              {step === 1 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Basic Information
                  </h3>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Institution Name *</Label>
                      <Input
                        {...form.register("name")}
                        placeholder="Enter institution name"
                        required
                      />
                    </div>
                    <div>
                      <Label>Institution Type</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("type", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {INSTITUTION_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Affiliation Status</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("affiliation_status", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select affiliation" />
                        </SelectTrigger>
                        <SelectContent>
                          {AFFILIATION_STATUS.map(status => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Year Established</Label>
                      <Input
                        type="number"
                        {...form.register("year_established")}
                        placeholder="e.g., 1990"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Contact Person Name</Label>
                    <Input
                      {...form.register("contact_person")}
                      placeholder="Enter contact person name"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        {...form.register("email")}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        {...form.register("phone")}
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Website</Label>
                    <Input
                      {...form.register("website")}
                      placeholder="https://"
                    />
                  </div>

                  <Button type="submit" className="w-full" size="lg">
                    Continue
                  </Button>
                </form>
              )}

              {/* Step 2: Address Information */}
              {step === 2 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Address Information
                  </h3>

                  <div>
                    <Label>Address Line 1</Label>
                    <Input
                      {...form.register("address_line1")}
                      placeholder="Building name, street address"
                    />
                  </div>

                  <div>
                    <Label>Address Line 2</Label>
                    <Input
                      {...form.register("address_line2")}
                      placeholder="Area, landmark"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>City</Label>
                      <Input
                        {...form.register("city")}
                        placeholder="Enter city"
                      />
                    </div>
                    <div>
                      <Label>District</Label>
                      <Input
                        {...form.register("district")}
                        placeholder="Enter district"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>State</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("state", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDIAN_STATES.map(state => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        {...form.register("pincode")}
                        placeholder="Enter pincode"
                        maxLength={6}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Continue
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 3: Academic Details */}
              {step === 3 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Academic Details
                  </h3>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Board / University</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("board", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select board" />
                        </SelectTrigger>
                        <SelectContent>
                          {BOARDS.map(board => (
                            <SelectItem key={board.value} value={board.value}>
                              {board.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Medium of Instruction</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("medium", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select medium" />
                        </SelectTrigger>
                        <SelectContent>
                          {MEDIUM_OF_INSTRUCTION.map(medium => (
                            <SelectItem key={medium.value} value={medium.value}>
                              {medium.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Accreditation</Label>
                    <Select
                      onValueChange={(value: string) =>
                        form.setValue("accreditation", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select accreditation" />
                      </SelectTrigger>
                      <SelectContent>
                        {ACCREDITATION.map(acc => (
                          <SelectItem key={acc.value} value={acc.value}>
                            {acc.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Education Levels Offered</Label>
                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                      {INSTITUTION_LEVELS.map(level => (
                        <div
                          key={level.value}
                          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                            selectedLevels.includes(level.value)
                              ? "border-primary bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => toggleLevel(level.value)}
                        >
                          <CheckCircle
                            className={`h-4 w-4 ${selectedLevels.includes(level.value) ? "opacity-100" : "opacity-0"}`}
                          />
                          <span className="text-sm">{level.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Affiliation Number</Label>
                      <Input
                        {...form.register("affiliation_number")}
                        placeholder="Enter affiliation number"
                      />
                    </div>
                    <div>
                      <Label>Recognition Number</Label>
                      <Input
                        {...form.register("recognition_number")}
                        placeholder="Enter recognition number"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Continue
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 4: Capacity & Facilities */}
              {step === 4 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Capacity & Facilities
                  </h3>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label>Total Students</Label>
                      <Input
                        type="number"
                        {...form.register("total_students")}
                        placeholder="Enter count"
                      />
                    </div>
                    <div>
                      <Label>Teaching Staff</Label>
                      <Input
                        type="number"
                        {...form.register("teaching_staff")}
                        placeholder="Enter count"
                      />
                    </div>
                    <div>
                      <Label>Non-Teaching Staff</Label>
                      <Input
                        type="number"
                        {...form.register("non_teaching_staff")}
                        placeholder="Enter count"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Number of Classrooms</Label>
                      <Input
                        type="number"
                        {...form.register("classrooms")}
                        placeholder="Enter count"
                      />
                    </div>
                    <div>
                      <Label>Campus Area (in sq ft)</Label>
                      <Input
                        type="number"
                        {...form.register("campus_area")}
                        placeholder="Enter area"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Departments / Faculties</Label>
                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                      {DEPARTMENTS.map(dept => (
                        <div
                          key={dept}
                          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                            selectedDepartments.includes(dept)
                              ? "border-primary bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => toggleDepartment(dept)}
                        >
                          <CheckCircle
                            className={`h-4 w-4 ${selectedDepartments.includes(dept) ? "opacity-100" : "opacity-0"}`}
                          />
                          <span className="text-sm">{dept}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Facilities Available</Label>
                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                      {FACILITIES.map(facility => (
                        <div
                          key={facility}
                          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                            selectedFacilities.includes(facility)
                              ? "border-primary bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => toggleFacility(facility)}
                        >
                          <CheckCircle
                            className={`h-4 w-4 ${selectedFacilities.includes(facility) ? "opacity-100" : "opacity-0"}`}
                          />
                          <span className="text-sm">{facility}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Continue
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 5: Trustee Information */}
              {step === 5 && (
                <form
                  onSubmit={form.handleSubmit(handleFinalSubmit)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Trustee / Management Information
                  </h3>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Trustee / Principal Name</Label>
                      <Input
                        {...form.register("trustee_name")}
                        placeholder="Enter name"
                      />
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("trustee_designation", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select designation" />
                        </SelectTrigger>
                        <SelectContent>
                          {TRUSTEE_DESIGNATIONS.map(designation => (
                            <SelectItem
                              key={designation.value}
                              value={designation.value}
                            >
                              {designation.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Trustee Phone</Label>
                      <Input
                        {...form.register("trustee_contact")}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <Label>Trustee Email</Label>
                      <Input
                        type="email"
                        {...form.register("trustee_email")}
                        placeholder="Enter email"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Number of Trustees</Label>
                      <Input
                        type="number"
                        {...form.register("trustee_count")}
                        placeholder="Enter count"
                      />
                    </div>
                    <div>
                      <Label>Trust Registration Number</Label>
                      <Input
                        {...form.register("trust_registration")}
                        placeholder="Enter registration number"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Names of Other Trustees</Label>
                    <Textarea
                      {...form.register("other_trustees")}
                      placeholder="Enter names separated by comma"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label>Additional Comments</Label>
                    <Textarea
                      {...form.register("comments")}
                      placeholder="Any additional information about your institution..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Submit Registration
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
